/**
 * Created by robert.lavoie on 6/21/17.
 */

import { Component, OnInit, OnDestroy, Input, Inject, PLATFORM_ID, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { PaginationInstance } from 'ngx-pagination/dist/ngx-pagination.module';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { URLSearchParams } from '@angular/http';
import { Subject ,  Subscription } from 'rxjs';
import { filter, distinct, switchMap } from 'rxjs/operators';
import * as Raven from 'raven-js';

// Services
import { JobsSearchService, SiteService, LocationSearchService, GeoLocationService, ReportingService } from '../../services';
import { ScriptService } from 'snagajob/src/services/scripts/script.service';

// Models
import { Filter, JobSearchParams, JobSearchQueryParams, JobQueryParams, LatLng, SelectedJob } from '../../models';

// Global state variable
import { lastSearchState } from '../../models/job-query-params';


const enum AmountOfJobsFor {
  list = 10,
  map = 100
}

interface Window {
  google: any;
}

declare var window: Window;

/**
 *
 * @class
 * JobsSearchComponent
 *
 * @description
 * This is the parent component for all the jobs search components
 *
 */
@Component({
  templateUrl: './jobs-search.component.html',
  selector: 'jobs-search',
  styleUrls: ['./jobs-search.component.scss']
})
export class JobsSearchComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('jobSearchFilterMenu') jobSearchFilterMenu;
  @ViewChild('jobResults') jobResults;
  @ViewChild('mapView') mapView;

  // The sites theme color
  @Input() themeColor: string;

  // Subscribes to query string changes
  private sub;

  // The params are sent within the body of the jobs/search post req
  private jobSearchParams: JobSearchParams = {};

  // The params are sent as query params to the jobs/search post req
  private jobSearchQueryParams: JobSearchQueryParams;

  private sortFilter: Filter = {
    headline : 'Sort by',
    className : 'sort-by',
    animatedState : 'up',
    type: 'radio',
    defaultOptions : [
      {
        displayName: 'Distance',
        selected: true,
        name: 'distance',
        value: 'distance'
      },
      {
        displayName: 'Job Title',
        selected: false,
        name: 'jobtitle',
        value: 'jobtitle'
      }
    ],
    options: []
  };

  private distanceFilter: Filter = {
    headline : 'Search Radius',
    className : 'search-radius',
    animatedState : 'up',
    type: 'radio',
    defaultOptions : [
      {
        displayName: '5 miles',
        name: '5-miles',
        selected: false,
        value: '5'
      },
      {
        displayName: '10 miles',
        name: '10-miles',
        selected: false,
        value: '10'
      },
      {
        displayName: '25 miles',
        name: '25-miles',
        selected: false,
        value: '25'
      },
      {
        displayName: '50 miles',
        name: '50-miles',
        selected: false,
        value: '50'
      },
      {
        displayName: 'Everywhere',
        name: '1000-miles',
        selected: true,
        value: '1000'
      }
    ],
    options: []
  };

  private jobTitleFilter: Filter;

  private jobTypeFilter: Filter;

  // The radius used for all map location based searches
  private mapRadius: number = +this.distanceFilter.defaultOptions[1].value;

  // an array of all filters
  public filters: Filter[];

  // The allowed view types
  public viewTypes: String[] = ['list', 'map'];

  // There are two view types available either list or map
  public currentViewType: string;

  // This is the total selected options from the jobTitleFilter and jobTypeFilter
  // This number is displayed on the mobile filter menu toggle button
  public totalSelectedOptions: number = 0;

  // The result of a job search
  public jobsSearchResults: object;

  // States if a search had been made. This is helpful to hide no-results info
  public hasListSearched: boolean = false;
  public hasMapSearched: boolean = false;

  // is set to true while a search is happening
  public isSearching: boolean = false;

  // Whether or not to show distance on the search results
  public showDistance: boolean;

  // Array of job results of your search
  public jobsSearchResultsList: object[] = [];

  // Array of job results of your search for the maps
  public jobsSearchResultsMap: object[] = [];

  // When set to true the location dropdown will slide open
  public locationDropdownAnimatedState: boolean = false;

  // We we want to know when we are toggling views and searching
  public isTogglingViews: boolean = false;

  // determins when we can see the maps component
  public googleMapsIsLoaded: boolean = false;

  // set to true when trying to load google maps
  public googleMapsIsLoading: boolean = false;

  // The config for ngx-pagination pipe
  public config: PaginationInstance = {
    id: 'job-search-pagination',
    itemsPerPage: AmountOfJobsFor.list,
    currentPage: 1,
    totalItems: null,
  };

// Returns the total number of jobs
  public actualJobTotal: number;

  // Form Controls for the jobSearch form
  public location = new FormControl('', [Validators.required]);

  // The job search form
  public jobSearch: FormGroup = this.builder.group({
    location: this.location
  });

  // Subscribing to a job being clicked
  jobSubscription$: Subscription;

  private latestJobSearchParams$ = new Subject<object>();

  public isBrowser: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private builder: FormBuilder,
    private siteService: SiteService,
    private jobsSearchService: JobsSearchService,
    private scriptService: ScriptService,
    private locationSearchService: LocationSearchService,
    public geoLocationService: GeoLocationService,
    private reportingService: ReportingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {

    // Set the sort options based of the default ones, we need the defaultOptions when resetting filters
    this.distanceFilter.options = JSON.parse(JSON.stringify(this.distanceFilter.defaultOptions));
    this.sortFilter.options = JSON.parse(JSON.stringify(this.sortFilter.defaultOptions));


    // Subscribe to the latestJobSearchParams$ subject so when changes happen we can initiate a new search
    this.latestJobSearchParams$.pipe(
      distinct(),
      switchMap(() => {
        this.isSearching = true;
        return this.jobsSearchService.getJobs(this.jobSearchParams, this.jobSearchQueryParams);

      }))
      .subscribe(
        (data) => {
          this.populateSearchResults(data);
          this.trackPageView();
        },
        () => {
          this.isSearching = false;
          this.clearSearchResults();
        }

      );

    this.jobSubscription$ = this.jobsSearchService.jobSelected$.subscribe(
      (selectedJob: SelectedJob) => {
        this.searchResultClick(selectedJob);
      }
    );


  }

  ngOnInit() {

    // subscribe to query param changes
    this.sub = this.route
      .queryParams
      .subscribe((params) => {

        // Build search body params based of the query string
        this.jobSearchParams = {};

        // Build search query params based of the query string
        this.jobSearchQueryParams = {};

        // build an array of customer Ids used for search
        this.jobSearchParams.customerId = this.siteService.getSearchParams('billingAccountId');

        // build an array of brandTemplateIds used for search
        this.jobSearchParams.brandTemplateId = this.siteService.getSearchParams('brandTemplateId');

        // build an array of brandTemplateIds used for search
        this.jobSearchParams.brandId = this.siteService.getSearchParams('brandId');

        // Defaults to 0 if no query param provided.
        this.jobSearchParams.start = params['start'] || '0';

        // set the current page based of the starting number
        this.config.currentPage = Math.floor(this.jobSearchParams.start / AmountOfJobsFor.list + 1);

        // determine if provided num matches our job options
        if (params['view'] === this.viewTypes['0']) {
          this.jobSearchParams.num = AmountOfJobsFor.list;
          this.currentViewType = this.viewTypes['0'];
        } else if (params['view'] === this.viewTypes['1']) {
          this.jobSearchParams.num = AmountOfJobsFor.map;
          this.currentViewType = this.viewTypes['1'];
        } else {
          this.currentViewType = this.viewTypes['0'];
        }

        // determine if provided radius matches our distance options
        if (params['rad']) {

          let matchingRadiusIndex: number;
          this.distanceFilter['options'].forEach((data, index) => {
            // determine if the supplied query param is a match
            if (params['rad'] === data.value){
              matchingRadiusIndex = index;
            }
          });


          // if we find a match then unselect default and select match
          if (matchingRadiusIndex >= 0) {
            this.distanceFilter['options'].forEach((data, index) => {
              this.distanceFilter['options'][index]['selected'] = params['rad'] === data.value ? true : false;
            });

            this.jobSearchParams.rad = params['rad'];

          }

        } else {
          this.jobSearchParams.rad = +this.distanceFilter['defaultOptions'].find(option => option['selected'] === true).value;
        }

        // determine if provided sort matches our sort options
        if (params['sort']) {
          let matchingSortIndex: number;
          this.sortFilter['defaultOptions'].forEach((data, index) => {
            // determine if the supplied query param is a match
            if (params['sort'] === data.value){
              matchingSortIndex = index;
            }
          });


          // if we find a match then unselect default and select match
          if (matchingSortIndex >= 0) {
            this.sortFilter['options'].forEach((data, index) => {
              this.sortFilter['options'][index]['selected'] = params['sort'] === data.value ? true : false;
            });

            this.jobSearchParams.sort = params['sort'];

          }

        } else {
          this.jobSearchParams.sort = this.sortFilter['defaultOptions'].find(option => option['selected'] === true).value;
        }

        if (params['jobTypes']) {
          this.jobSearchQueryParams.jobTypes = params['jobTypes'].split(',');
        }

        if (params['jobTitles']) {
          this.jobSearchQueryParams.jobTitles = params['jobTitles'].split(',');
        }

        if (params['location']) {
          const location = decodeURIComponent(params['location']);
          this.jobSearchParams.location = location;
          this.jobSearch.patchValue({ location });

        } else if (params['lat'] && params['lng']) {
          this.jobSearchParams.lat = params['lat'];
          this.jobSearchParams.lng = params['lng'];

        } else if (params['ne'] && params['sw']) {
          this.jobSearchParams.ne = params['ne'];
          this.jobSearchParams.sw = params['sw'];
        }

        if (this.currentViewType === this.viewTypes['0']){
          if (params['location'] || params['lat'] && params['lng']){
            this.latestJobSearchParams$.next({ ...this.jobSearchParams,  ...this.jobSearchQueryParams });
          } else if (params['ne'] && params['sw']){
            if (!window.google) {
              this.scriptService.add({
                name: 'googlemaps',
                src: 'https://maps.googleapis.com/maps/api/js?key=AIzaSyAwE8rWpqTjm4ZlyOGCLrc4ZLZx2jGjHV4',
                async: true,
                defer: true
              });

              this.scriptService.loadScript('googlemaps').then(() => {

                this.googleMapsIsLoaded = true;
                this.jobSearchParams.polygons = this.convertCoordsToPolygon(params['ne'], params['sw']);
                this.latestJobSearchParams$.next({ ...this.jobSearchParams,  ...this.jobSearchQueryParams });
              });

            } else {
              this.jobSearchParams.polygons = this.convertCoordsToPolygon(params['ne'], params['sw']);
              this.latestJobSearchParams$.next({ ...this.jobSearchParams,  ...this.jobSearchQueryParams });
            }

          } else {
            // only fall back to an IP search for a list view
            this.getLocationFromIP();
          }

        }

        if (this.currentViewType === this.viewTypes['1']){

          if (!window.google) {

            this.scriptService.add({
              name: 'googlemaps',
              src: 'https://maps.googleapis.com/maps/api/js?key=AIzaSyAwE8rWpqTjm4ZlyOGCLrc4ZLZx2jGjHV4',
              async: true,
              defer: true
            });

            this.scriptService.loadScript('googlemaps').then(() => {
              this.googleMapsIsLoaded = true;
              if (params['ne'] && params['sw']){
                this.jobSearchParams.polygons = this.convertCoordsToPolygon(params['ne'], params['sw']);
              }

              if (params['location'] || params['lat'] && params['lng'] || params['ne'] && params['sw']){
                this.latestJobSearchParams$.next({ ...this.jobSearchParams,  ...this.jobSearchQueryParams });
              }
            });

          } else {
            this.googleMapsIsLoaded = true;
            if (params['ne'] && params['sw']){
              this.jobSearchParams.polygons = this.convertCoordsToPolygon(params['ne'], params['sw']);
            }

            if (params['location'] || params['lat'] && params['lng'] || params['ne'] && params['sw']){
              this.latestJobSearchParams$.next({ ...this.jobSearchParams,  ...this.jobSearchQueryParams });
            }
          }

        }

      });

  }



  /**
   * Lifecycle hook
   */
  ngAfterViewInit() {
    // If there is a fragment, handle the anchor functionality
    // This is temporary because of https://github.com/angular/angular/issues/13636
    const tree = this.router.parseUrl(this.router.url);
    if (tree.fragment && tree.fragment == 'job-results' && this.jobResults) {
      setTimeout(
        () => {
          this.scrollToResults();
        },
        350
        );
    }
  }


  /**
   * Lifecycle hook for cleanup
   */
  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    if (this.jobSubscription$) this.jobSubscription$.unsubscribe();
  }

  /**
   * Scrolls to search results if present
   */
  scrollToResults() {
    const element: Element = this.jobResults.nativeElement;
    if (element && element.scrollIntoView) {
      element.scrollIntoView();
    }
  }

  /**
   * Gets the lat lng coordinates from the browser, then initiates a search based of the coordinates
   */
  getLatLngFromGeoLocation(){

    // clear the location
    this.jobSearch.patchValue({ location: '' });
    this.jobSearchParams.location = null;
    this.isSearching = true;

    this.geoLocationService.getLocation()
      .subscribe(
        (data) => {
          // set the lat and lng
          this.jobSearchParams.lat = data.coords.latitude;
          this.jobSearchParams.lng = data.coords.longitude;

          this.updateQueryParams();


        },
        (err) => {
          this.isSearching = false;
        }
      );

    this.reportingService.trackCustomEvent('cp_mylocation');
  }


  /**
   * Gets the location of a user based of there IP, then initiates a search based of that location
   */
  getLocationFromIP(){
    this.locationSearchService.getLocationDataFromIP()
      .subscribe(
        (data) => {
          // Validate that we have a list object which contains the location information, then update the location
          if (data) {

            // Build up the geolocation string with as much info as we have
            const dataToAdd = [];
            if (data.city) dataToAdd.push(data.city);
            if (data.stateProvCode) dataToAdd.push(data.stateProvCode);
            this.jobSearchParams.location = dataToAdd.join(', ');

          } else {

            // Track invalid IP messages
            Raven.captureMessage('Invalid IP', {
              level: 'info'
            });

          }

          this.search(this.jobSearchParams, this.jobSearchQueryParams);

        },
        () => {
          // If we cant find users IP then just do a generic search without an updated location
          this.search(this.jobSearchParams, this.jobSearchQueryParams);
        }
      );
  }

  /**
   * Gets a list of jobs based of your search criteria and filters
   *
   * @param { Object: JobSearchParams }
   * @param { Object: JobSearchQueryParams }
   */
  search(jobSearchParams: JobSearchParams, jobSearchQueryParams: JobSearchQueryParams){

    this.isSearching = true;
    this.jobsSearchService.getJobs(jobSearchParams, jobSearchQueryParams)
    .subscribe(
        (data) => {
          this.populateSearchResults(data);
          this.trackPageView();

        },
        () => {
          this.isSearching = false;
          this.clearSearchResults();
        }
      );

  }


  /**
   * Once a search has completed we create filters and assign results
   *
   * @param { object }
   */

  populateSearchResults(data: object){

    // make sure the lastSearchState has been initialized for deeplinking
    if (Object.keys(lastSearchState.params).length === 0 && lastSearchState.params.constructor === Object) {
      this.updateQueryParams(this.config.currentPage, true);
    }

    this.isSearching = false;
    this.isTogglingViews = false;

    this.clearAllFilters();
    if (this.currentViewType === this.viewTypes['0']){
      this.hasListSearched = true;
      this.createDefaultFilters();
    } else {
      this.hasMapSearched = true;
      this.jobsSearchResultsMap = data['list'];
    }

    // Create the list of filters to be used on desktop or mobile
    this.createFacetFilters(data['facetList']);

    if (data['list'].length > 0) {

      // Set showDistance to true if we have data from the API to show
      this.showDistance = data['latitude'] && data['longitude'] ? true : false;

      this.jobsSearchResults = data;

      this.actualJobTotal = data['actualTotal'];

      if (this.currentViewType === this.viewTypes['0']){
        this.jobsSearchResultsList = data['list'];
        this.config.totalItems = data['total'];

      } else {
        this.jobsSearchResultsMap = data['list'];
        this.config.totalItems = data['list'].length;
      }

    } else {
      // No results found
      this.clearSearchResults();
    }
  }

  /**
   * Tracks the page view with appropriate parameters
   */
  trackPageView() {
    // Update location to be 'my_loc' if using geolocation
    let location = this.jobSearchParams.location;

    // TODO THIS IS NO LONGER ACURITE
    if (this.jobSearchParams.lat && this.jobSearchParams.lng) {
      location = 'my_loc';
    }

    // Set page name dynamically based on view type
    let pageName = 'cp-home-results';
    if (this.currentViewType === 'map') pageName = 'cp-home-results-map';

    // Call the reporting service
    if (isPlatformBrowser(this.platformId)) {
      this.reportingService.pageView({
        pageName,
        eVar10: location,
        eVar12: this.config.currentPage,
        eVar13: this.actualJobTotal,
        eVar19: this.siteService.getOwnerId(),
        prop10: location,
        prop12: this.config.currentPage,
        prop13: this.actualJobTotal,
        prop19: this.siteService.getOwnerId(),
      });

      const queryStringParams = new URLSearchParams();
      for (const param in this.jobSearchParams) {
        queryStringParams.set(param, this.jobSearchParams[param]);
      }

      this.reportingService.internalEvent({
        EventType: 'Search',
        Placement: 'Keyword Search',
        PostingId: this.jobsSearchResultsList.map((el) => { return el['id']; }).join(),
        QueryString: queryStringParams.toString(),
        PageNumber: this.config.currentPage,
        UserInitiated: null, // (true or false based on if the user initiated the search manually),
      });
    }
  }


  /**
   * Clears any results data
   */
  clearSearchResults() {
    this.config.totalItems = null;
    this.actualJobTotal = null;
    this.jobsSearchResults = {};
    this.jobsSearchResultsMap = [];
    this.jobsSearchResultsList = [];
  }


  /**
   * Get the current search params based of the selected filters
   *
   * @param { number }  Specifies the result to start with when calling a collection resource
   * @return { object: JobQueryParams }
   */
  getQueryParams(resultStart: number): JobQueryParams {

    const jobQueryParams: JobQueryParams = {
      start: this.currentViewType === this.viewTypes['0'] ? resultStart : 0,
      rad: this.currentViewType === this.viewTypes['0'] ? +this.distanceFilter['options'].find(option => option['selected'] === true).value : this.mapRadius,
      // tslint:disable:max-line-length
      sort: this.currentViewType === this.viewTypes['0'] ? this.sortFilter['options'].find(option => option['selected'] === true).value : this.sortFilter['defaultOptions'].find(option => option['selected'] === true).value,
      view: this.currentViewType
    };

    if (this.location.value || this.jobSearchParams.location){
      jobQueryParams.location = this.location.value || this.jobSearchParams.location;
    } else if (this.jobSearchParams.lat && this.jobSearchParams.lng) {
      jobQueryParams.lat = this.jobSearchParams.lat;
      jobQueryParams.lng = this.jobSearchParams.lng;
    } else if (this.jobSearchParams.ne && this.jobSearchParams.sw) {
      jobQueryParams.ne = this.jobSearchParams.ne;
      jobQueryParams.sw = this.jobSearchParams.sw;
    }

    if (this.jobTitleFilter){
      // Create a comma seperated list of job titles values that are selected
      const jobTitles = this.jobTitleFilter['options'].reduce((previous, current) => {

        if (current['selected'] === true){
          return previous ? previous + ',' + current['value'] : current['value'];
        } else {
          return previous;
        }

      },                                                      '');

      if (jobTitles){
        jobQueryParams.jobTitles = jobTitles;
      }

    }

    if (this.jobTypeFilter) {

      // Create a comma seperated list of job type values that are selected
      const jobTypes = this.jobTypeFilter['options'].reduce((previous, current) => {

        if (current['selected'] === true){
          return previous ? previous + ',' + current['value'] : current['value'];
        } else {
          return previous;
        }

      },                                                    '');

      if (jobTypes){
        jobQueryParams.jobTypes = jobTypes;
      }

    }

    return jobQueryParams;

  }


  /**
   * Creates default filters
   */
  createDefaultFilters(){
    this.filters.push(this.distanceFilter, this.sortFilter);
  }



  /**
   * Creates filters based of facets from the current search
   * We also manage the selected state of the filters
   *
   * @param { Array }
   */
  createFacetFilters(facetList: any[]) {

    // clear facet filters
    this.jobTitleFilter = null;
    this.jobTypeFilter = null;

    // Create the jobTitleFilter by grabbing the category facet from the response and adding it to the filters array
    const jobTitleFacet = facetList.find(facet => facet['name'] === 'jobtitle');

    if (jobTitleFacet) {

      // Get the total number of selected options for a given filter
      let selectedJobTitleOptionsCount: number = 0;

      const jobTitleOptions = jobTitleFacet['values'].map((data) => {

        const options = { displayName : data.displayValue, name: data.value, value: data.value, selected: false };

        // select any options that are set in the query string
        if (this.jobSearchQueryParams.jobTitles){

          if (this.jobSearchQueryParams.jobTitles.indexOf(data.value) > -1){
            options.selected = true;
            selectedJobTitleOptionsCount += 1;
          }
        }

        return options;
      });

      this.jobTitleFilter = {
        headline : 'Job title',
        className : 'job-title',
        animatedState : 'up',
        type: 'checkbox',
        selectedOptionsCount: selectedJobTitleOptionsCount,
        options : jobTitleOptions,
        defaultOptions: jobTitleOptions
      };


      this.totalSelectedOptions += selectedJobTitleOptionsCount;

      this.filters.push(this.jobTitleFilter);

    }

    // Create the categoryFilter by grabbing the category facet from the response and adding it to the filters array
    const categoryFacet = facetList.find(facet => facet['name'] === 'category');

    if (categoryFacet) {

      // Get the total number of selected options for a given filter
      let selectedJobTypeOptionsCount: number = 0;

      const jobTypeOptions = categoryFacet['values'].map((data) => {
        const options = { displayName : data.displayValue, name: data.value, value: data.value, selected: false };


        // select any options that are set in the query string
        if (this.jobSearchQueryParams.jobTypes){

          if (this.jobSearchQueryParams.jobTypes.indexOf(data.value) > -1){
            options.selected = true;
            selectedJobTypeOptionsCount += 1;
          }
        }

        return options;
      });

      this.jobTypeFilter = {
        headline : 'Job type',
        className : 'job-type',
        animatedState : 'up',
        type: 'checkbox',
        selectedOptionsCount: selectedJobTypeOptionsCount,
        options : jobTypeOptions,
        defaultOptions: jobTypeOptions
      };

      this.totalSelectedOptions += selectedJobTypeOptionsCount;

      // add the jobTypeFilter to the list of filters
      this.filters.push(this.jobTypeFilter);

    }

  }


  /**
   * Apply's the new filters by updating the query params
   *
   * @param { number } page number for search results
   * @param { boolean } updates the lastSearchState but skips the routing
   */
  updateQueryParams(page: number = 1, skipRouter: boolean = false){
    const resultStart = (page - 1) * this.config.itemsPerPage;
    this.config.currentPage = page;

    // updating the query params
    const searchParams = this.getQueryParams(resultStart);
    lastSearchState.params = this.getQueryParams(resultStart);
    if (searchParams.location) {
      searchParams.location = searchParams.location;
      // Replace smart punctuation on ios
      searchParams.location = searchParams.location.replace(/[\u2018\u2019]/g, '\'');
      searchParams.location = searchParams.location.replace(/[\u201C\u201D]/g, '\"');
    }
    if (!skipRouter) {
      this.router.navigate([''], { queryParams: searchParams, fragment: 'job-results' });
      this.scrollToResults();
    }
  }


  /**
   * Clears the filters
   */
  clearAllFilters(){
    this.filters = [];
  }


  /**
   * Resets all the filters to the default state and updates the query params
   */
  resetAllFilters(){

    this.filters.forEach((data) => {
      this.reset(data);
    });

    this.updateQueryParams();
  }


  /**
   * Resets a filter to the default state
   *
   * @param { Filter } The filter to reset
   *
   */
  reset(filter: Filter){

    // If the filter is a radio then set the options to the filters default options
    if (filter.type === 'radio'){
      filter.options = JSON.parse(JSON.stringify(filter.defaultOptions));
    }

    // If the filter is a checkbox the unselect all options
    if (filter.type === 'checkbox'){
      filter[ 'options' ].forEach((data, index) => {
        filter[ 'options' ][ index ][ 'selected' ] = false;
      });
    }
  }


  /**
   * Resets a Filter and updates the query params
   *
   * @param { Filter } The filter to reset
   *
   */
  resetFilter(filter: Filter){
    this.reset(filter);
    this.updateQueryParams();
  }


  /**
   * Updated the search results view type
   *
   * @param { string } Possible values are either List or Map
   */
  updateViewType(view: string){
    this.currentViewType = view;
    this.isTogglingViews = true;
    this.updateQueryParams();
    this.clearAllFilters();
    this.clearSearchResults();

    // Tracking the view toggle
    if (view === 'list') this.reportingService.trackCustomEvent('cp_listview');
    else if (view === 'map') this.reportingService.trackCustomEvent('cp_mapview');
  }

  /**
   * Toggles the location dropdown animated state
   */
  toggleLocationDropdownAnimatedState(){
    this.locationDropdownAnimatedState = !this.locationDropdownAnimatedState;
  }

  /**
   * Clears out the location data and performs a new search based on ne and sw which get converted to a polygon
   *
   * @param { Array: LatLng[] } Array of Arrays with Lat and Lng cords
   */
  newMapCoords(coords: { ne: LatLng; sw: LatLng; }){

    // clear current location details
    this.jobSearch.patchValue({ location: '' });
    this.jobSearchParams.location = undefined;
    this.jobSearchParams.lat = undefined;
    this.jobSearchParams.lng = undefined;
    this.jobSearchParams.rad = undefined;
    this.jobSearchParams.ne = `${coords.ne.lat},${coords.ne.lng}`;
    this.jobSearchParams.sw = `${coords.sw.lat},${coords.sw.lng}`;
    this.updateQueryParams();
  }

  /**
   * Converts ne and sw stings to a polygon for a polygon based search
   *
   * @param { string } - NorthEast LatLng coords split by comma
   * @param { string } - SouthWest LatLng coords split by comma
   * @param { LatLng[][] } - Array of Arrays with Lat and Lng coords
   */
  convertCoordsToPolygon(ne: string, sw: string){

    const NE: LatLng = {
      lat: +ne.split(',')[0],
      lng: +ne.split(',')[1],
    };

    const SW: LatLng = {
      lat: +sw.split(',')[0],
      lng: +sw.split(',')[1],
    };

    const NW = new window.google.maps.LatLng(NE.lat, SW.lng);
    const SE = new window.google.maps.LatLng(SW.lat, NE.lng);

    // Creates polygons
    const polygon: LatLng[][] = [[
      { lat : NE.lat, lng : NE.lng },
      { lat : NW.lat(), lng : NW.lng() },
      { lat : SW.lat, lng : SW.lng },
      { lat : SE.lat(), lng : SE.lng() },
      { lat : NE.lat, lng : NE.lng }
    ]];

    return polygon;
  }



  /**
   * Job click handler
   *
   * @param { selectedJob: selectedJob }
   */
  searchResultClick(selectedJob: SelectedJob) {
    this.reportingService.setJobPostingIndex(selectedJob['index']);
    this.reportingService.trackCustomEvent(selectedJob['tracking']);
    this.router.navigate(['/job-details/' + selectedJob['job']['id']]);
  }

}
